import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { IconChevron, IconCancel, IconPendoInfo } from 'components/Icons';
import IconButton from '../IconButton';
import { useTipsCarousel } from './useTipsCarousel';
import { TipItem } from './tips';
import './TipsCard.scss';

const prefix = prefixClass('ksq2-tips-card');

interface TipsCardProps {
  tips: TipItem[];
  isOpen: boolean;
  onClose: () => void;
}

export const TipsCard = ({ tips, isOpen, onClose }: TipsCardProps) => {
  const { currentIndex, canGoBack, canGoForward, isLastTip, goNext, goPrev, goTo, swipeHandlers } = useTipsCarousel({
    totalItems: tips.length,
    onClose,
  });

  const currentTip = tips[currentIndex];

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
        case 'ArrowRight':
          goNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goNext, goPrev]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleGotIt = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen || !currentTip) return null;

  return createPortal(
    <div className={prefix('__backdrop')} onClick={handleBackdropClick}>
      <div className={prefix()} {...swipeHandlers}>
        {/* Close button */}

        <IconButton icon={<IconCancel />} onClick={onClose} size="small" className={prefix('__close')} />

        {/* Left navigation */}
        <IconButton
          icon={<IconChevron />}
          onClick={goPrev}
          disabled={!canGoBack}
          size="large"
          className={classNames(prefix('__nav'), prefix('__nav--left'))}
        />

        {/* Content */}
        <div className={prefix('__content')}>
          {/* Title - first row */}
          <div className={prefix('__title')}>
            <IconPendoInfo />
            <span>Did you know?</span>
          </div>

          {/* Body - flex-grow centered */}
          <div className={prefix('__body')}>
            {/* Illustration */}
            <div className={prefix('__illustration')}>{currentTip.illustration}</div>

            {/* Text content */}
            <div className={prefix('__text')}>
              <div className={prefix('__subtitle')}>{currentTip.title}</div>
              <div className={prefix('__description')}>{currentTip.description}</div>
            </div>

            {/* Got it button (on last tip) */}
            {isLastTip && (
              <button className={prefix('__got-it')} onClick={handleGotIt}>
                Got it
              </button>
            )}
          </div>

          {/* Navigation dots - last row */}
          <div className={prefix('__dots')}>
            {tips.map((_, index) => (
              <button
                key={index}
                className={classNames(prefix('__dot'), {
                  [prefix('__dot--active')]: index === currentIndex,
                })}
                onClick={() => goTo(index)}
                aria-label={`Go to tip ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Right navigation */}
        <IconButton
          icon={<IconChevron />}
          onClick={goNext}
          disabled={!canGoForward}
          size="large"
          className={classNames(prefix('__nav'))}
        />
      </div>
    </div>,
    document.body
  );
};

export default TipsCard;
